import { createApi } from '@reduxjs/toolkit/query/react';
import { PageInfoParams, PaginatedResponsePageInfo, UpdateGrid } from '../types';
import { baseQuery } from './BaseApi';
import { panelApi } from './PanelService';
import { projectApi } from './ProjectService';

// Define a service using a base URL and expected endpoints
export const pageApi = createApi({
	reducerPath: 'pageApi',
	baseQuery: baseQuery,
	tagTypes: ['Projects', 'PageInfo'],
	endpoints: (builder) => ({
		getPageInfos: builder.query<PaginatedResponsePageInfo, Partial<PageInfoParams>>({
			query: (params) => ({
				url: `v1/pageInfo/`,
				params,
			}),
			providesTags: (result) =>
				result?.content
					? [
							...result.content.map(({ id }) => ({ type: 'PageInfo' as const, id })),
							{ type: 'PageInfo', id: 'LIST' },
					  ]
					: [{ type: 'PageInfo', id: 'LIST' }],
		}),
		updateGridPage: builder.mutation<UpdateGrid, any>({
			query: ({ pageId, body }) => ({
				url: `v1/pages/${pageId}/updateGridLayout`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted({ pageId, ...patch }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					panelApi.util.updateQueryData('getPanels', pageId, (draft) => {
						Object.assign(draft, patch);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(panelApi.util.invalidateTags([{ type: 'Panels', id: 'LIST' }]));
				} catch {
					patchResult.undo();

					/**
					 * Alternatively, on failure you can invalidate the corresponding cache tags
					 * to trigger a re-fetch:
					 * dispatch(api.util.invalidateTags(['Post']))
					 */
				}
			},
		}),
		getAllPages:builder.query({
			query: () => ({
				url:`v1/pages/`,
				providesTags: (result) =>
					result?.content
						? [
							...result.content.map(({ id }) => ({ type: 'Panels' as const, id })),
							{ type: 'Pages', id: 'LIST' },
						]
						: [{ type: 'Pages', id: 'LIST' }],
			})
		}),
		createPage:builder.mutation({
			query: (params) => ({
				url:`v1/pages/`,
				method:'POST',
				body: params,
			}),
			async onQueryStarted( {pageId, body }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					projectApi.util.updateQueryData('getProjects', pageId, (draft) => {
						Object.assign(draft, body);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(projectApi.util.invalidateTags([{ type: 'Project', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
		}),
		editPage:builder.mutation({
			query: ({pageId, body}) => ({
				url:`v1/pages/${pageId}`,
				method:'PUT',
				body,
			}),
			async onQueryStarted( {pageId, body }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					projectApi.util.updateQueryData('getProjects', pageId, (draft) => {
						Object.assign(draft, body);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(projectApi.util.invalidateTags([{ type: 'Project', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
		}),
		deletePage:builder.mutation({
			query: (pageId) => ({
				url:`v1/pages/${pageId}`,
				method:'DELETE',
			}),
			async onQueryStarted( {pageId, body }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					projectApi.util.updateQueryData('getProjects', pageId, (draft) => {
						Object.assign(draft, body);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(projectApi.util.invalidateTags([{ type: 'Project', id: 'LIST' }]));
				} catch {
					patchResult.undo();
				}
			},
		}),
		getPageById:builder.query({
			query: (pageId) => ({
				url:`v1/pages/${pageId}`,
				providesTags: (result) =>
					result?.content
						? [
							...result.content.map(({ id }) => ({ type: 'Panels' as const, id })),
							{ type: 'Pages', id: 'LIST' },
						]
						: [{ type: 'Pages', id: 'LIST' }],
			})
		}),
	}),
});

export default pageApi.reducer;

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetPageInfosQuery, useUpdateGridPageMutation, useCreatePageMutation, useGetPageByIdQuery, useEditPageMutation, useDeletePageMutation } = pageApi;
